import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'ds-check',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="ds-check"
      type="button"
      role="checkbox"
      [class.ds-check--checked]="checked"
      [disabled]="disabled"
      [attr.aria-checked]="checked"
      [attr.aria-label]="label ? null : ariaLabel"
      (click)="toggle()"
      (focus)="focused.emit($event)"
      (blur)="blurred.emit($event)"
    >
      <span class="ds-check__control" aria-hidden="true">
        <span class="ds-check__indicator">
          <svg class="ds-check__mark" viewBox="0 0 11.44 8.862" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 4.65L4.25 7.862L10.44 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </span>

      @if (label) {
        <span class="ds-check__label">{{ label }}</span>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      line-height: 0;
    }

    .ds-check {
      display: inline-flex;
      align-items: center;
      gap: var(--ds-check-label-gap);
      padding: 0;
      border: 0;
      background: transparent;
      box-sizing: border-box;
      color: var(--ds-check-check-color);
      cursor: pointer;
      font-family: inherit;
      text-align: start;
      transition: opacity var(--ds-check-transition-duration) ease;
    }

    .ds-check__control {
      width: var(--ds-check-container-size);
      height: var(--ds-check-container-size);
      display: inline-grid;
      place-items: center;
      border: 0 solid transparent;
      border-radius: var(--ds-check-container-radius);
      background: transparent;
      box-sizing: border-box;
      color: var(--ds-check-check-color);
      transition:
        background-color var(--ds-check-transition-duration) ease,
        border-color var(--ds-check-transition-duration) ease,
        border-width var(--ds-check-transition-duration) ease;
    }

    .ds-check:hover:not(:disabled) .ds-check__control {
      border-width: var(--ds-check-hover-border-width);
      border-color: var(--ds-check-hover-border-color);
      background: var(--ds-check-hover-background);
    }

    .ds-check:focus-visible {
      outline: none;
    }

    .ds-check:focus-visible .ds-check__control {
      border-width: var(--ds-check-focus-border-width);
      border-color: var(--ds-check-focus-border-color);
    }

    .ds-check:disabled {
      opacity: calc(var(--ds-check-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-check__indicator {
      width: var(--ds-check-size);
      height: var(--ds-check-size);
      display: inline-grid;
      place-items: center;
      border: var(--ds-check-border-width) solid var(--ds-check-border-color);
      border-radius: var(--ds-check-border-radius);
      background: var(--ds-check-background);
      box-sizing: border-box;
      transition:
        background-color var(--ds-check-transition-duration) ease,
        border-color var(--ds-check-transition-duration) ease;
    }

    .ds-check--checked .ds-check__indicator {
      background: var(--ds-check-selected-background);
    }

    .ds-check__mark {
      width: var(--ds-check-check-width);
      height: var(--ds-check-check-height);
      color: var(--ds-check-check-color);
      opacity: 0;
      transform: scale(0.85);
      transition:
        opacity var(--ds-check-transition-duration) ease,
        transform var(--ds-check-transition-duration) ease;
    }

    .ds-check--checked .ds-check__mark {
      opacity: 1;
      transform: scale(1);
    }

    .ds-check__label {
      color: var(--ds-check-label-color);
      font-size: var(--ds-check-label-font-size);
      font-weight: var(--ds-check-label-font-weight);
      line-height: var(--ds-check-label-line-height);
      padding-inline-end: var(--ds-check-label-gap);
    }
  `],
})
export class DsCheckComponent {
  @Input() checked = false;
  @Input() disabled = false;
  @Input() ariaLabel = 'Check';
  @Input() label = '';

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  toggle(): void {
    if (this.disabled) return;
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
  }
}
